/* eslint-disable */
import React, { useState, useRef, useEffect } from "react";
import {
  Menu,
  Button,
  Group,
  Flex,
  Box,
  Text,
  Divider,
  Portal,
} from "@mantine/core";
import { RangeCalendar } from "@mantine/dates";
import { IconChevronDown, IconCalendar } from "@tabler/icons-react";

type RangeOption =
  | "Today"
  | "Yesterday"
  | "Last 7 Days"
  | "Last 30 Days"
  | "This Month"
  | "Last Month"
  | "Custom Range";

export interface DateRange {
  start: string | null;
  end: string | null;
}

interface DateRangeSelectorProps {
  onDateRangeChange?: (dateRange: DateRange) => void;
  selectedType: RangeOption;
  setSelectedType?: (val: RangeOption) => void;
  selectedRange: DateRange;
}

const options: RangeOption[] = [
  "Today",
  "Yesterday",
  "Last 7 Days",
  "Last 30 Days",
  "This Month",
  "Last Month",
  "Custom Range",
];

export default function DateRangeSelector({
  onDateRangeChange,
  selectedType,
  setSelectedType,
  selectedRange,
}: DateRangeSelectorProps) {
  const today = new Date();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const formatDate = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleDateString("en-CA");
  };

  const getPresetRange = (option: RangeOption): DateRange => {
    const now = new Date();
    switch (option) {
      case "Today":
        return { start: formatDate(now), end: formatDate(now) };
      case "Yesterday": {
        const y = new Date(now.getTime() - 86400000);
        return { start: formatDate(y), end: formatDate(y) };
      }
      case "Last 7 Days":
        return {
          start: formatDate(new Date(now.getTime() - 6 * 86400000)),
          end: formatDate(now),
        };
      case "Last 30 Days":
        return {
          start: formatDate(new Date(now.getTime() - 29 * 86400000)),
          end: formatDate(now),
        };
      case "This Month":
        return {
          start: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)),
          end: formatDate(now),
        };
      case "Last Month":
        return {
          start: formatDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
          end: formatDate(new Date(now.getFullYear(), now.getMonth(), 0)),
        };
      case "Custom Range":
        return selectedRange;
      default:
        return { start: null, end: null };
    }
  };

  const [selected, setSelected] = useState<RangeOption>(selectedType);
  const [range, setRange] = useState<DateRange>(getPresetRange(selectedType));
  const [customOpen, setCustomOpen] = useState(false);
  const [temp, setTemp] = useState<[Date | null, Date | null]>([
    range.start ? new Date(range.start) : null,
    range.end ? new Date(range.end) : null,
  ]);

  // Calculate position when popover opens
  useEffect(() => {
    if (customOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popoverWidth = 700; // Approximate width for 2-month calendar
      const viewportWidth = window.innerWidth;

      // Start position shifted 100px to the left from the button
      let left = rect.left - 100;

      // If popover would go off-screen on the right, align it to the right edge
      if (left + popoverWidth > viewportWidth - 20) {
        left = viewportWidth - popoverWidth - 20;
      }

      // Make sure it doesn't go off-screen on the left
      if (left < 20) {
        left = 20;
      }

      setPopoverPosition({
        top: rect.bottom + 8,
        left: left,
      });
    }
  }, [customOpen]);

  // --- Handle Preset Select ---
  const handleSelect = (opt: RangeOption) => {
    setSelected(opt);
    setSelectedType?.(opt);
    if (opt === "Custom Range") {
      setCustomOpen(true);
      return;
    }
    const newRange = getPresetRange(opt);
    setRange(newRange);
    onDateRangeChange?.(newRange);
  };

  // --- Apply Custom ---
  const applyCustom = () => {
    if (temp[0] && temp[1]) {
      const formatted = {
        start: formatDate(temp[0]),
        end: formatDate(temp[1]),
      };
      setRange(formatted);
      setSelected("Custom Range");
      setSelectedType?.("Custom Range");
      onDateRangeChange?.(formatted);
      setTimeout(() => setCustomOpen(false), 100);
    }
  };

  // --- Label logic ---
  const getButtonLabel = () => {
    if (selected === "Custom Range" && range.start && range.end) {
      return `${range.start} → ${range.end}`;
    }
    return selected;
  };

  return (
    <Box style={{ position: "relative" }}>
      <Menu shadow="md" width={180} zIndex={9999} withinPortal>
        <Menu.Target>
          <Button
            ref={buttonRef}
            variant="default"
            size="sm"
            px="sm"
            style={{
              fontWeight: 400,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "18px",
                width: "100%",
              }}
            >
              <IconCalendar size={16} />
              <div style={{ display: "flex" }}>
                <span>{getButtonLabel()}</span>
                <IconChevronDown size={14} style={{ marginLeft: "2px" }} />
              </div>
            </div>
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          {options.map((opt) => (
            <Menu.Item
              key={opt}
              onClick={() => handleSelect(opt)}
              color={opt === selected ? "blue" : undefined}
            >
              {opt}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      {/* Custom Range Popover - Using Portal */}
      {customOpen && (
        <Portal>
          {/* Backdrop */}
          <Box
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            }}
            onClick={() => setCustomOpen(false)}
          />

          {/* Popover Content */}
          <Box
            ref={popoverRef}
            style={{
              position: "fixed",
              top: popoverPosition.top,
              left: popoverPosition.left,
              zIndex: 9999,
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
              padding: "16px",
              border: "1px solid #e9ecef",
              maxWidth: "calc(100vw - 40px)",
            }}
            onClick={(e: any) => e.stopPropagation()}
          >
            <Text size="sm" fw={500} mb="xs">
              Custom Range
            </Text>
            <Divider mb="sm" />

            <RangeCalendar
              value={temp}
              onChange={setTemp}
              amountOfMonths={2}
              maxDate={today}
              size="sm"
            />

            {temp[0] && temp[1] && (
              <Text size="xs" mt="sm" ta="center" c="dimmed">
                {formatDate(temp[0])} → {formatDate(temp[1])}
              </Text>
            )}

            <Flex justify="flex-end" mt="md" gap="xs">
              <Button
                size="xs"
                variant="subtle"
                onClick={() => setCustomOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                color="blue"
                onClick={applyCustom}
                disabled={!temp[0] || !temp[1]}
              >
                Apply
              </Button>
            </Flex>
          </Box>
        </Portal>
      )}
    </Box>
  );
}
